'use client';

import { useEffect, useRef } from 'react';
import { useLocale } from 'next-intl';
import { useUserProfile } from '@/modules/shared/api';
import { useUpdateUserConfig } from '@/modules/setting/api';
import { useBoundStore } from '@/modules/shared/stores/store';
import { useQueryClient } from '@tanstack/react-query';

/**
 * LocaleSyncProvider
 * 
 * Syncs the client locale (from cookie) with the server-side user profile.
 * When a user lands on the app, if their profile locale doesn't match
 * the current locale, we update their profile to match.
 * 
 * This handles cases like:
 * - User changed language on another device
 * - User cleared cookies but profile has a preference
 * - Migration from old locale system
 */
export function LocaleSyncProvider({ children }: { children: React.ReactNode }) {
  const locale = useLocale();
  const accessToken = useBoundStore((s) => s.accessToken);
  const { data: profile } = useUserProfile(!!accessToken);
  const { mutateAsync: updateUserConfig } = useUpdateUserConfig(profile?.id);
  const hasSynced = useRef(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Only sync once per session
    if (hasSynced.current) return;
    
    // Wait for profile to load and ensure user is authenticated
    if (!profile || !accessToken) return;

    const profileLocale = profile.userConfig?.language;
    
    // If locales don't match, update the server to match client locale
    if (profileLocale && profileLocale !== locale) {
      console.log(`Syncing locale: profile has "${profileLocale}", but client locale is "${locale}". Updating profile...`);
      
      updateUserConfig({
        body: {
          language: locale,
        },
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['userProfile'] });
        console.log('Locale synced successfully');
        hasSynced.current = true;
      }).catch((error) => {
        console.error('Failed to sync locale:', error);
      });
    } else {
      // Locales match, no sync needed
      hasSynced.current = true;
    }
  }, [locale, profile, accessToken, updateUserConfig]);

  return <>{children}</>;
}
